export const BOOKING_ACTIVITY_TYPES = ["guest", "owner", "block"];

export function isBookingActivity(activity) {
  return BOOKING_ACTIVITY_TYPES.includes(activity?.type);
}

export function calendarActivityKey(activity, resourceId = "") {
  const id =
    activity?.occurrenceKey ||
    activity?.workOrderId ||
    activity?.reservationId ||
    activity?.id ||
    activity?.label ||
    "activity";
  return `${resourceId}:${activity?.type || "item"}:${id}:${
    activity?.date || activity?.checkIn || ""
  }`;
}

export function initials(value, fallback = "•") {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return fallback;
  const first = parts[0]?.[0] || "";
  const second =
    parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : parts[0]?.[1] || "";
  return `${first}${second}`.toUpperCase() || fallback;
}

export function formatTimeValue(value) {
  if (!value) return "";
  const match = String(value).match(/(?:T|\s)?(\d{1,2}):(\d{2})/);
  if (!match) return "";
  const hour = Number(match[1]);
  const minute = match[2];
  if (!Number.isFinite(hour)) return "";
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minute} ${suffix}`;
}

export function formatActivityTimeRange(activity) {
  const start = formatTimeValue(activity?.scheduledStartTime);
  const end = formatTimeValue(activity?.scheduledEndTime);
  if (start && end) return `${start}–${end}`;
  return start || end || "";
}

export function parseCalendarSearch(search = "") {
  const params = new URLSearchParams(search);
  const allowedRanges = new Set(["3-day", "7-day", "14-day", "30-day"]);
  const range = params.get("range");
  const date = params.get("date");
  return {
    activeView: allowedRanges.has(range) ? range : "7-day",
    startDate: /^\d{4}-\d{2}-\d{2}$/.test(date || "") ? date : null,
    density: params.get("density") === "collapsed" ? "collapsed" : "expanded",
    criteria: {
      type: params.get("type") || "",
      priority: params.get("priority") || "",
      property: params.get("property") || "",
      user: params.get("user") || "",
      property_group: params.get("property_group") || "",
      status: params.get("status") || "",
      assignment: params.get("assignment") || "",
      recurrence: params.get("recurrence") || "",
      search_term: params.get("q") || "",
      hide_completed: params.get("hide_completed") === "1",
      // Empty rows are hidden by default. An explicit 0 keeps a user's
      // opt-out stable when the URL is copied or refreshed.
      hide_empty: params.get("hide_empty") !== "0",
      show_bookings: params.get("show_bookings") !== "0",
    },
  };
}

export function buildCalendarSearch({
  activeView,
  startDate,
  density,
  criteria,
}) {
  const params = new URLSearchParams();
  if (activeView && activeView !== "7-day") params.set("range", activeView);
  if (startDate) params.set("date", startDate);
  if (density === "collapsed") params.set("density", density);

  const mappings = {
    type: "type",
    priority: "priority",
    property: "property",
    user: "user",
    property_group: "property_group",
    status: "status",
    assignment: "assignment",
    recurrence: "recurrence",
    search_term: "q",
  };
  Object.entries(mappings).forEach(([key, param]) => {
    if (criteria?.[key]) params.set(param, criteria[key]);
  });
  if (criteria?.hide_completed) params.set("hide_completed", "1");
  if (criteria?.hide_empty === false) params.set("hide_empty", "0");
  if (criteria?.show_bookings === false) params.set("show_bookings", "0");
  const value = params.toString();
  return value ? `?${value}` : "";
}

export function readinessFromPropertyStatus(propertyStatus) {
  const status = String(propertyStatus?.status || propertyStatus || "").toLowerCase();
  if (status === "ready") return { state: "ready", label: "Ready" };
  if (status === "needs_attention") {
    return { state: "risk", label: "Needs attention" };
  }
  if (status === "out_of_service") {
    return { state: "blocked", label: "Out of service" };
  }
  return null;
}

export function propertyReadinessForActivities(
  homeId,
  activities,
  today,
  propertyStatus = null,
) {
  const explicitReadiness = readinessFromPropertyStatus(propertyStatus);
  if (explicitReadiness) return explicitReadiness;

  const list = (activities || []).filter(
    (activity) => String(activity.homeId) === String(homeId),
  );
  const hasActiveBlock = list.some((activity) => {
    if (activity.type !== "block") return false;
    const start = String(activity.checkIn || "").slice(0, 10);
    const end = String(activity.checkOut || start).slice(0, 10);
    return start <= today && end >= today;
  });
  if (hasActiveBlock) {
    return { state: "blocked", label: "Blocked" };
  }

  const arrivals = list.filter(
    (activity) =>
      activity.type === "guest" &&
      String(activity.checkIn || "").slice(0, 10) === today,
  );
  if (!arrivals.length) return null;

  const arrivalWork = list.filter(
    (activity) =>
      ["housekeeping", "inspection"].includes(activity.type) &&
      String(activity.date || "").slice(0, 10) === today,
  );
  if (!arrivalWork.length) {
    return { state: "risk", label: "At risk · no turnover task" };
  }
  if (
    arrivalWork.every((activity) =>
      ["completed", "closed"].includes(String(activity.status || "").toLowerCase()),
    )
  ) {
    return { state: "ready", label: "Ready" };
  }
  return { state: "on-track", label: "On track" };
}
